/**
 * Hook de auto-reload por version.
 *
 * Decision (2026-05-27): los despliegues los hacemos cuando los usuarios NO
 * estan usando la app, y estamos en fase pre-publica. Por eso el reload es
 * silencioso y automatico — sin banner, sin confirmacion. Si en el futuro
 * pasamos a publico y queremos UX mas amable, agregar un banner antes del
 * window.location.reload().
 *
 * IMPORTANTE (2026-07-01): NO recargar mientras el usuario está en una sesión
 * activa (hablando con Galia). El reload silencioso a mitad de conversación se
 * percibía como "Galia se cierra sola y me devuelve a la pantalla de config"
 * (reporte de Santiago). Si detectamos versión nueva durante una sesión,
 * DIFERIMOS la recarga y la ejecutamos recién cuando el usuario vuelve al menú
 * (sessionActive pasa a false). Así toma el deploy nuevo sin ser interrumpido.
 *
 * REFORZADO (2026-07-02): una profesora vio en clase la barra vieja (4 iconos
 * en vez de 6) durante TODA la sesión. Causas estructurales que esto arregla:
 *
 *  1. El primer check era recién a los 60s. Si el usuario entraba a la sesión
 *     antes de ese primer check, la recarga quedaba diferida toda la clase.
 *     Ahora el primer check corre a los ~1.5s del arranque, cuando el usuario
 *     todavía está en la pantalla de configuración (momento seguro).
 *
 *  2. La página nunca reaccionaba a que el service worker nuevo tomara control
 *     (controllerchange). El flujo PWA normal es: visita N sirve el bundle
 *     viejo desde el precache y actualiza el SW en background; el bundle nuevo
 *     recién se ve en la visita N+1. Con el listener, apenas el SW nuevo toma
 *     control recargamos (o diferimos si hay sesión activa) — el deploy se ve
 *     en la MISMA visita, incluso si el poll de version.json fallara.
 *
 *  3. Anti-bucle + autocuración: si el SW queda atascado sirviendo el bundle
 *     viejo (p.ej. el install del precache falla siempre por red/cuota), el
 *     poll detecta versión nueva, recarga… y vuelve a caer en el bundle viejo,
 *     en bucle. Ahora, tras 2 recargas sin converger hacia la misma versión
 *     remota, se desregistra el SW, se borran los caches y se recarga UNA vez
 *     más directo desde red (equivale a un hard refresh automático). Si ni así
 *     converge, dejamos de intentar: mejor app vieja funcionando que un bucle
 *     de recargas.
 *
 * Mecanica del poll:
 *  - Cada POLL_INTERVAL_MS, fetch a /version.json (sin cache; el SW nunca lo
 *    precachea — ver globIgnores/navigateFallbackDenylist en vite.config.ts).
 *  - Si el `version` del JSON difiere del baked-in `__BUILD_VERSION__` del
 *    bundle actual -> hay un deploy nuevo en Vercel pero este cliente sigue
 *    corriendo el bundle viejo -> recargar (o diferir).
 *  - Si hay un SW nuevo instalándose en ese momento, se le da margen (hasta
 *    2 ticks) para que termine y dispare controllerchange, en vez de recargar
 *    a mitad de instalación y desperdiciar la recarga.
 *  - Errores de red se silencian: la app no debe romperse si Vercel se cae.
 *  - Dev mode: skip total. HMR ya maneja recargas locales.
 *
 * Una sola instancia activa por tab (guard `pollStarted`) — si se monta dos
 * veces (StrictMode), no duplicamos interval ni listener.
 *
 * @param sessionActive true mientras el usuario está en conversación con Galia
 *   (initialConfigComplete). Cuando es false está en el menú/config: momento
 *   seguro para recargar.
 */

import { useEffect, useRef } from 'react';

const FIRST_CHECK_DELAY_MS = 1_500; // primer check casi al arranque, aún en pantalla de config
const POLL_INTERVAL_MS = 60_000; // 60s: balance entre frescura y trafico.

// Estado anti-bucle en sessionStorage (sobrevive a los reloads de la misma
// pestaña, se resetea al abrir pestaña nueva): { v: versión remota que
// intentamos alcanzar, n: recargas ya intentadas hacia esa versión }.
const RELOAD_STATE_KEY = 'galia_version_reload';
const MAX_PLAIN_RELOADS = 2; // tras esto, escalar a limpieza de SW + caches
const MAX_TOTAL_RELOADS = 3; // tras esto, rendirse (nunca bucles infinitos)

let pollStarted = false;

function readReloadAttempts(target: string): number {
  try {
    const raw = sessionStorage.getItem(RELOAD_STATE_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { v?: string; n?: number };
    return parsed.v === target ? parsed.n ?? 0 : 0;
  } catch {
    return 0;
  }
}

function writeReloadAttempts(target: string, attempts: number): void {
  try {
    sessionStorage.setItem(RELOAD_STATE_KEY, JSON.stringify({ v: target, n: attempts }));
  } catch {
    // sessionStorage bloqueado (modo privado estricto): sin contador el
    // anti-bucle no escala a limpieza, pero las recargas siguen espaciadas
    // por el poll. Aceptable.
  }
}

function clearReloadAttempts(): void {
  try {
    sessionStorage.removeItem(RELOAD_STATE_KEY);
  } catch {
    // idem
  }
}

/**
 * Hard reset del PWA: desregistra los service workers y borra CacheStorage.
 * Es el equivalente programático de "borrar datos del sitio" + hard refresh.
 * Solo se usa cuando el SW quedó atascado sirviendo un bundle viejo.
 */
async function unregisterSwAndClearCaches(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // seguir igual: el reload posterior es lo importante
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // idem
  }
}

/**
 * Recarga la página para alcanzar la versión `target`, con anti-bucle:
 * recargas 1 y 2 son normales; la 3.ª va precedida de limpieza total de
 * SW/caches; a partir de ahí no se intenta más en esta pestaña.
 */
async function reloadForVersion(target: string): Promise<void> {
  const attempts = readReloadAttempts(target);
  if (attempts >= MAX_TOTAL_RELOADS) {
    // eslint-disable-next-line no-console
    console.warn(
      `[VersionCheck] seguimos en ${__BUILD_VERSION__} tras ${attempts} recargas (objetivo ${target}). No se reintenta más en esta pestaña.`,
    );
    return;
  }
  writeReloadAttempts(target, attempts + 1);
  if (attempts >= MAX_PLAIN_RELOADS) {
    // eslint-disable-next-line no-console
    console.warn(
      '[VersionCheck] el service worker parece atascado sirviendo un bundle viejo. Limpiando SW + caches y recargando desde red...',
    );
    await unregisterSwAndClearCaches();
  }
  window.location.reload();
}

export function useVersionCheck(sessionActive: boolean): void {
  // Recarga pendiente: se detectó versión nueva pero el usuario estaba en
  // sesión, así que esperamos a que salga del chat para recargar.
  const pendingReloadRef = useRef(false);
  // Versión remota hacia la que va la recarga pendiente (para el anti-bucle).
  const pendingVersionRef = useRef<string>('');
  // Ref espejo de sessionActive para leerlo dentro del setInterval sin
  // recrear el interval en cada cambio de estado.
  const sessionActiveRef = useRef(sessionActive);
  sessionActiveRef.current = sessionActive;

  // Poll de versión + listener de controllerchange (se montan una sola vez).
  useEffect(() => {
    // En dev __BUILD_VERSION__ existe (lo emite el plugin) pero no hay
    // /version.json servido por el dev server -> el fetch fallaria siempre y
    // no aporta nada. Saltamos.
    if (import.meta.env.DEV) return;
    if (pollStarted) return;
    pollStarted = true;

    // Recargar ya si es seguro; diferir si el usuario está en plena sesión.
    const requestReload = (target: string, reason: string) => {
      if (sessionActiveRef.current) {
        if (!pendingReloadRef.current) {
          pendingReloadRef.current = true;
          pendingVersionRef.current = target;
          // eslint-disable-next-line no-console
          console.log(`[VersionCheck] ${reason}. Recarga diferida (sesión activa).`);
        }
        return;
      }
      // Log antes de recargar — queda visible en la consola si alguien
      // esta debuggeando.
      // eslint-disable-next-line no-console
      console.log(`[VersionCheck] ${reason}. Recargando...`);
      void reloadForVersion(target);
    };

    // Si hay un SW nuevo a mitad de instalación, darle hasta 2 ticks para que
    // termine y dispare controllerchange (recarga limpia) antes de recargar a
    // ciegas. Si la instalación falla, el worker `installing` desaparece y el
    // siguiente tick recarga por el camino normal.
    let installingGraceLeft = 2;

    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const data = (await res.json()) as { version?: string };
        if (!data.version) return;
        if (data.version === __BUILD_VERSION__) {
          // Al día: limpiar el contador anti-bucle de recargas previas.
          clearReloadAttempts();
          return;
        }
        if ('serviceWorker' in navigator && installingGraceLeft > 0) {
          try {
            const reg = await navigator.serviceWorker.getRegistration();
            if (reg && (reg.installing || reg.waiting)) {
              installingGraceLeft--;
              // eslint-disable-next-line no-console
              console.log(
                `[VersionCheck] nueva version ${data.version} detectada; el SW nuevo aún se está instalando, esperando su controllerchange...`,
              );
              return;
            }
          } catch {
            // sin acceso a la registration: seguir con la recarga normal
          }
        }
        requestReload(
          data.version,
          `nueva version disponible: ${__BUILD_VERSION__} -> ${data.version}`,
        );
      } catch {
        // Silenciado adrede — no romper la app si version.json no responde.
      }
    };

    // Primer check casi inmediato: el usuario todavía está en la pantalla de
    // configuración, que es el momento seguro para recargar. (Antes el primer
    // check era a los 60s y el usuario ya solía estar dentro de la sesión, con
    // lo que la recarga quedaba diferida toda la clase.)
    const firstCheckId = window.setTimeout(check, FIRST_CHECK_DELAY_MS);
    const intervalId = window.setInterval(check, POLL_INTERVAL_MS);

    // Camino rápido PWA: el navegador detectó un deploy, instaló el SW nuevo y
    // este tomó control (skipWaiting + clientsClaim). La página sigue corriendo
    // el bundle viejo -> recargar para servir el nuevo YA, en esta visita.
    // Guard `wasControlled`: en la primera visita (o tras la limpieza de SW) la
    // página arranca sin controller; el claim inicial también dispara
    // controllerchange, pero ahí el contenido ya vino fresco de red y recargar
    // sería un reload en vano.
    const wasControlled =
      'serviceWorker' in navigator && !!navigator.serviceWorker.controller;
    const onControllerChange = () => {
      if (!wasControlled) return;
      requestReload('sw-update', 'el service worker nuevo tomó control de la página');
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    }

    return () => {
      window.clearTimeout(firstCheckId);
      window.clearInterval(intervalId);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      }
      pollStarted = false;
    };
  }, []);

  // Cuando el usuario sale de la sesión (vuelve al menú) y había una recarga
  // pendiente, recargar ahora: es el momento seguro, no lo interrumpe.
  useEffect(() => {
    if (!sessionActive && pendingReloadRef.current) {
      pendingReloadRef.current = false;
      // eslint-disable-next-line no-console
      console.log('[VersionCheck] usuario salió de la sesión. Aplicando recarga diferida...');
      void reloadForVersion(pendingVersionRef.current || 'pending');
      pendingVersionRef.current = '';
    }
  }, [sessionActive]);
}
